import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { drw, errText, photoUrl, canDeleteMarkup } from "./api";
import { idb } from "../fieldops/offline/db";
import { removeRecord } from "../fieldops/offline/outbox";

const QUEUED = "Saved on this device — syncs when signal returns";

const Row = ({ k, v }) => (v ? <div className="flex gap-2 text-xs"><span className="w-20 shrink-0 text-neutral-500">{k}</span><span className="break-words">{v}</span></div> : null);

/** The selected markup: its Bluebeam metadata, replies, resolve and carry-forward decision. It
 *  makes no claim about an FRS item — a takeoff mark never opens this panel any more, it is a tap
 *  on the ticker (DRW-8). */
export default function MarkupDetail({ m, projectId, onClose, onChanged, onOpenFrs, onMoveRequest, onResizeRequest, canWrite, me }) {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const pending = !!m.pending;            // not on the server yet — nothing to reply to or resolve there
  const wcfg = { _project_id: projectId };
  const [thumb, setThumb] = useState("");
  useEffect(() => {
    if (!m.local_thumb) { setThumb(""); return undefined; }
    let url = "";
    idb.get("blobs", m.local_thumb).then((b) => { if (b) { url = URL.createObjectURL(b); setThumb(url); } });
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [m.local_thumb]);

  const call = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); if (r?.queued) toast.message(QUEUED); else if (ok) toast.success(ok); onChanged?.(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const mine = canDeleteMarkup(m);   // DRW-FIX-3 — the same sentence the server enforces
  // FT-1 — "Resolve" only means resolved on an ISSUE (Field Issue / RFI / Verify / punch / note).
  // Everywhere else the word is Installed, and the state reads as a checkmark, not a shade.
  const ISSUE_SUBJECTS = ["field issue", "rfi", "verify"];
  const isIssue = ["punch_pin", "sticky"].includes(m.type)
    || ISSUE_SUBJECTS.includes(String(m.subject || "").toLowerCase());
  // PLAN-COMPLETE §1 — the "Installed ✓ / Not installed" toggle is GONE. It was a per-mark
  // installed state stored outside the FRS, which the Install view could not see. Completion is
  // the FRS record's, reached by tapping the mark (ticker → the real completion dialog). Only an
  // ISSUE can still be resolved from here, which is not completion.
  const doneLabel = m.status === "open" ? "Resolve" : "Reopen";
  const canShape = ["cloud", "rectangle", "ellipse", "polygon"].includes(m.type) && m.origin === "app";
  return (
    <div className="bg-white border border-neutral-200 rounded-xl shadow-xl p-3 space-y-2 text-sm w-[19rem] max-h-[70vh] overflow-auto"
         data-testid="markup-detail">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate" data-testid="markup-detail-title">{m.subject || m.label || m.type}</div>
          <div className="text-[11px] text-neutral-500">{m.type} · {m.layer} · {m.author} · {(m.created_at || "").slice(0, 10)}</div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-neutral-100" data-testid="markup-detail-close"><X className="w-4 h-4" /></button>
      </div>
      {(pending || m.pending_edit) && (
        <div className={`rounded-lg border p-2 text-xs space-y-1 ${m.conflict ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="markup-detail-pending">
          <div className="font-semibold">{m.conflict ? "Server refused this — review it in Sync" : pending ? "Waiting to sync — placed with no signal" : "Change waiting to sync"}</div>
          {m.conflict_detail && <div>{m.conflict_detail}</div>}
          {pending && <button type="button" disabled={busy} onClick={() => call(() => removeRecord(m.id).then(onClose), "Discarded — never sent")}
                              className="underline" data-testid="markup-detail-discard">Discard it</button>}
        </div>
      )}
      {m.verify_state === "verify" && canWrite && (
        <div className="rounded-lg bg-orange-50 border border-orange-200 p-2 space-y-1" data-testid="markup-detail-verify">
          <div className="text-xs font-semibold text-orange-800">Placed on Rev {m.placed_on_revision} — verify</div>
          <div className="flex gap-1">
            {["keep", "move", "dismiss"].map((a) => (
              <button key={a} type="button" disabled={busy} data-testid={`carry-${a}`}
                      onClick={() => (a === "move" ? onMoveRequest?.(m) : call(() => drw.post(`/markups/${m.id}/carry`, { action: a }, wcfg), a === "keep" ? "Kept" : "Dismissed"))}
                      className="flex-1 text-xs py-1 rounded border bg-white hover:border-orange-400 capitalize">{a}</button>
            ))}
          </div>
        </div>
      )}
      <Row k="Label" v={m.label} />
      <Row k="Room" v={m.room_ref?.room_number ? `${m.room_ref.room_number} ${m.room_ref.room_name || ""}` : m.space_name} />
      <Row k="Comments" v={m.comments} />
      <Row k="Measure" v={m.scale_values?.text} />
      <Row k="Assigned" v={m.assigned_to} />
      {Object.entries(m.custom_columns || {}).map(([k, v]) => <Row key={k} k={k} v={v} />)}
      {m.type === "photo_pin" && m.document_id && (
        <img src={photoUrl(m.document_id)} alt="pin photo" className="rounded-lg w-full max-h-48 object-cover" data-testid="markup-detail-photo" />
      )}
      {m.type === "photo_pin" && thumb && (
        <img src={thumb} alt="pin photo (on this device)" className="rounded-lg w-full max-h-48 object-cover" data-testid="markup-detail-photo-local" />
      )}
      {m.type === "punch_pin" && <div className="text-xs text-red-700 font-medium">Punch #{m.punch_no} — in the Field Punch view</div>}
      {(m.replies || []).length > 0 && (
        <div className="space-y-1 border-t pt-1">
          {m.replies.map((r) => <div key={r.id} className="text-xs"><span className="font-medium">{r.author}</span> <span className="text-neutral-400">{(r.at || "").slice(0, 10)}</span><div>{r.text}</div></div>)}
        </div>
      )}
      {canWrite && !pending && (
        <form className="flex gap-1" onSubmit={(e) => { e.preventDefault(); if (!reply.trim()) return; call(() => drw.post(`/markups/${m.id}/replies`, { text: reply }, wcfg).then((r) => { setReply(""); return r; })); }}>
          <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply…" className="flex-1 h-8 text-xs border rounded-md px-2" data-testid="markup-detail-reply-input" />
          <button type="submit" disabled={busy} className="h-8 px-2 text-xs rounded-md bg-neutral-900 text-white" data-testid="markup-detail-reply-send">Send</button>
        </form>
      )}
      <div className="flex flex-wrap gap-1 pt-1 border-t">
        {m.room_ref?.room_number && (
          <button type="button" onClick={() => onOpenFrs?.(m.room_ref?.room_number || "", "")} className="text-xs px-3 min-h-11 sm:min-h-0 sm:py-1 rounded-md border" data-testid="markup-open-frs">Open in FRS</button>
        )}
        {canWrite && !pending && m.origin === "app" && (
          <button type="button" onClick={() => onMoveRequest?.(m)} className="text-xs px-3 min-h-11 sm:min-h-0 sm:py-1 rounded-md border" data-testid="markup-detail-move">Move</button>
        )}
        {canWrite && !pending && canShape && (
          <button type="button" onClick={() => onResizeRequest?.(m)} className="text-xs px-3 min-h-11 sm:min-h-0 sm:py-1 rounded-md border" data-testid="markup-detail-resize">Resize</button>
        )}
        {canWrite && !pending && isIssue && (
          <button type="button" disabled={busy} onClick={() => call(() => drw.patch(`/markups/${m.id}`, { status: m.status === "open" ? "resolved" : "open" }, wcfg))}
                  className="text-xs px-3 min-h-11 sm:min-h-0 sm:py-1 rounded-md border"
                  data-testid="markup-detail-toggle-status">{doneLabel}</button>
        )}
        {canWrite && m.origin === "app" && mine && !pending && (
          <button type="button" disabled={busy} onClick={() => call(() => drw.delete(`/markups/${m.id}`, wcfg).then((r) => { onClose(); return r; }), "Removed")}
                  className="text-xs px-3 min-h-11 sm:min-h-0 sm:py-1 rounded-md border text-red-700" data-testid="markup-detail-delete">Delete</button>
        )}
      </div>
    </div>
  );
}
